import * as React from "react";
import { FormControl, InputLabel, MenuItem, Select, Stack } from "@mui/material";
import styled from "styled-components";
import { resolveObjPathString } from "src/utils/resolve-path";
import type { FetchAction } from "src/components/template/types";

const StyledMenuItem = styled(MenuItem)`
    font-size: 12px;
`;

const TemplateDropdown = ({
    value,
    title,
    allValue,
    setValue,
    dynamicOptions,
    options,
    data,
}: {
    value: string | null;
    title?: string | null;
    allValue?: string | null;
    setValue: React.Dispatch<React.SetStateAction<string>>;
    dynamicOptions: string | null;
    options: { name: string; value: string }[] | null;
    data: FetchAction["data"] | Omit<FetchAction["data"], "meta">;
}) => {
    if (!dynamicOptions && !options) return null;
    const [optionsList, setOptionsList] = React.useState<{ name: string; value: string }[]>([]);

    const handleOptionChange = (event) => {
        setValue(event.target.value);
    };

    React.useEffect(() => {
        if (data && dynamicOptions && !options) {
            const [, propsPath] = dynamicOptions.split("$data.");
            const [beforeIteration, afterIteration] = propsPath.split("[*].");
            const dataPart = resolveObjPathString(data, beforeIteration);
            if (dataPart) {
                const opts = dataPart
                    .map((obj) => resolveObjPathString(obj, afterIteration))
                    .filter((i) => i);
                const defaultItem = dataPart.find((d) => d.default);
                const defaultItemValue =
                    defaultItem && resolveObjPathString(defaultItem, afterIteration);
                const optionSet = new Set(opts);
                if (defaultItemValue) {
                    optionSet.delete(defaultItemValue);
                }
                const uniqueOptions = Array.from(new Set(opts)).map((o) => ({ name: o, value: o }));
                uniqueOptions.unshift({
                    name: "All",
                    value: allValue || null,
                });
                if (defaultItemValue) {
                    uniqueOptions.unshift({
                        name: defaultItemValue,
                        value: defaultItemValue,
                    });
                    setValue(defaultItemValue);
                }

                setOptionsList(uniqueOptions as { name: string; value: string }[]);
            }
        }
    }, [data && JSON.stringify(data)]);

    React.useEffect(() => {
        if (options) {
            setOptionsList(options);
        }
    }, [options && JSON.stringify(options)]);

    if (!data) return null;

    return (
        <Stack
            direction="column"
            sx={{
                typography: "body1",
            }}
            spacing={2}
            justifyContent="space-between"
        >
            <Stack direction="row" alignItems="center">
                <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                    {title && <InputLabel id={title}>{title}</InputLabel>}
                    <Select
                        {...(title && { id: title })}
                        value={value}
                        label={title}
                        onChange={handleOptionChange}
                        displayEmpty
                        inputProps={{ "aria-label": "select team" }}
                        sx={{ fontSize: 12 }}
                    >
                        {optionsList.map((item) => {
                            if (item !== null)
                                return (
                                    <StyledMenuItem value={item.value} key={item.value}>
                                        {item.name}
                                    </StyledMenuItem>
                                );
                            return null;
                        })}
                    </Select>
                </FormControl>
            </Stack>
        </Stack>
    );
};

export default TemplateDropdown;
